import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { IconPlus, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';

const emptyRow = () => ({ name: '', cnpj: '', email: '', phone: '', address: '' });

/**
 * "Adicionar várias empresas" - dialog com uma tabela editável de linhas
 * que vira um único POST pro endpoint de importação em massa
 * (`organizations/import/`), que já existia no backend mas nunca tinha
 * UI própria (o antigo botão "Importar organizações" era um stub).
 */
const BulkCreateOrganizations = ({ open, onClose, appId, onImported }) => {
    const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const updateRow = (index, field, value) => {
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const addRow = () => setRows((prev) => [...prev, emptyRow()]);

    const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

    const handleClose = () => {
        setRows([emptyRow(), emptyRow(), emptyRow()]);
        setResult(null);
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        const validRows = rows.filter((row) => row.name.trim());
        if (validRows.length === 0) {
            setError('Preencha ao menos o nome de uma empresa.');
            return;
        }

        setError('');
        setSaving(true);
        setResult(null);

        try {
            const response = await api.post(`v1/apps/${appId}/organizations/import/`, { organizations: validRows });
            setResult(response.data);
            if (response.data.created_count > 0) {
                onImported();
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Erro ao importar empresas.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>Adicionar várias empresas</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {result && (
                    <Alert severity={result.error_count > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                        {result.created_count} empresa(s) criada(s).
                        {result.error_count > 0 && ` ${result.error_count} linha(s) com erro:`}
                        {result.errors?.length > 0 && (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {result.errors.map((e, i) => (
                                    <li key={i}>
                                        Linha {e.row}: {e.error}
                                    </li>
                                ))}
                            </Box>
                        )}
                    </Alert>
                )}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome Fantasia</TableCell>
                            <TableCell>CNPJ</TableCell>
                            <TableCell>Telefone</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Endereço</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={row.name}
                                        onChange={(e) => updateRow(index, 'name', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={row.cnpj}
                                        onChange={(e) => updateRow(index, 'cnpj', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={row.phone}
                                        onChange={(e) => updateRow(index, 'phone', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={row.email}
                                        onChange={(e) => updateRow(index, 'email', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={row.address}
                                        onChange={(e) => updateRow(index, 'address', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => removeRow(index)} disabled={rows.length === 1}>
                                        <IconTrash size={16} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Stack direction="row" mt={1}>
                    <Button size="small" startIcon={<IconPlus size={16} />} onClick={addRow}>
                        Adicionar linha
                    </Button>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                    Linhas em branco (sem nome) são ignoradas.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Fechar</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Importando...' : 'Importar empresas'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

BulkCreateOrganizations.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    appId: PropTypes.string,
    onImported: PropTypes.func.isRequired
};

export default BulkCreateOrganizations;
